import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Briefcase, Plus, Edit, Trash2, Eye, EyeOff, 
  Users, MapPin, Clock, DollarSign, CheckCircle, X, Building2, FolderOpen, MessageSquare, Settings, FileText,
  GripVertical
} from 'lucide-react';
import AdminSidebar from '../components/layout/AdminSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

// DnD Kit imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sortable Job Item Component
const SortableJobItem = ({ job, onTogglePublish, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className={`p-6 hover:bg-slate-50 ${isDragging ? 'bg-slate-100' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <button
            {...attributes}
            {...listeners}
            className="p-1 cursor-grab hover:bg-slate-200 rounded active:cursor-grabbing mt-1"
            title="Drag to reorder"
          >
            <GripVertical className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900">{job.title}</h3>
              {job.is_published ? (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Published</span>
              ) : (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Draft</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />{job.department}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />{job.location}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />{job.employment_type}
              </span>
              {job.salary_range && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />{job.salary_range}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />{job.application_count} applications
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onTogglePublish(job)}
            title={job.is_published ? 'Unpublish' : 'Publish'}
          >
            {job.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onEdit(job)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => onDelete(job.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminJobsPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    employment_type: 'full-time',
    location: '',
    salary_range: '',
    description: '',
    responsibilities: [''],
    requirements: [''],
    benefits: [''],
    is_published: false
  });

  const departments = ['Construction', 'Office', 'Management', 'Design', 'Operations'];
  const employmentTypes = ['full-time', 'part-time', 'contract', 'temporary'];

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    fetchJobs();
  }, [navigate]);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/jobs`);
      // Sort by order if available, otherwise by created_at
      const sortedJobs = response.data.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setJobs(sortedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = jobs.findIndex(j => j.id === active.id);
      const newIndex = jobs.findIndex(j => j.id === over.id);
      const newJobs = arrayMove(jobs, oldIndex, newIndex);
      
      setJobs(newJobs);
      
      try {
        const reorderData = newJobs.map((job, index) => ({
          id: job.id,
          order: index + 1
        }));
        
        await axios.put(`${BACKEND_URL}/api/jobs/reorder`, { items: reorderData });
        toast.success('Job postings reordered');
      } catch (error) {
        console.error('Error reordering jobs:', error);
        toast.error('Failed to save job order');
        fetchJobs();
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanData = {
        ...formData,
        responsibilities: formData.responsibilities.filter(r => r.trim()),
        requirements: formData.requirements.filter(r => r.trim()),
        benefits: formData.benefits.filter(b => b.trim())
      };

      if (editingJob) {
        await axios.put(`${BACKEND_URL}/api/jobs/${editingJob.id}`, cleanData);
        toast.success('Job posting updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/jobs`, cleanData);
        toast.success('Job posting created');
      }
      fetchJobs();
      closeModal();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error('Failed to save job posting');
    }
  };

  const togglePublish = async (job) => {
    try {
      await axios.put(`${BACKEND_URL}/api/jobs/${job.id}`, { is_published: !job.is_published });
      toast.success(job.is_published ? 'Job unpublished' : 'Job published');
      fetchJobs();
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Failed to update job');
    }
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/jobs/${id}`);
      toast.success('Job posting deleted');
      fetchJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error('Failed to delete job');
    }
  };

  const openModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title,
        department: job.department,
        employment_type: job.employment_type,
        location: job.location,
        salary_range: job.salary_range || '',
        description: job.description,
        responsibilities: job.responsibilities.length ? job.responsibilities : [''],
        requirements: job.requirements.length ? job.requirements : [''],
        benefits: job.benefits?.length ? job.benefits : [''],
        is_published: job.is_published
      });
    } else {
      setEditingJob(null);
      setFormData({
        title: '',
        department: '',
        employment_type: 'full-time',
        location: 'Charlotte Metro Area',
        salary_range: '',
        description: '',
        responsibilities: [''],
        requirements: [''],
        benefits: [''],
        is_published: false
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
  };

  const addListItem = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ''] });
  };

  const updateListItem = (field, index, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const removeListItem = (field, index) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated.length ? updated : [''] });
  };

  const publishedCount = jobs.filter(j => j.is_published).length;
  const draftCount = jobs.filter(j => !j.is_published).length;

  return (
    <AdminSidebar pageTitle="Job Postings" pageIcon={FileText}>
      {/* Header Actions */}
      <div className="flex justify-end mb-6">
        <Button onClick={() => openModal()} className="bg-cyan-600 hover:bg-cyan-700">
          <Plus className="w-4 h-4 mr-2" />
          Create Job Posting
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <p className="text-3xl font-bold text-slate-900">{jobs.length}</p>
          <p className="text-slate-600">Total Jobs</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <p className="text-3xl font-bold text-green-600">{publishedCount}</p>
          <p className="text-slate-600">Published</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <p className="text-3xl font-bold text-yellow-600">{draftCount}</p>
          <p className="text-slate-600">Drafts</p>
        </div>
      </div>

      {/* Drag and Drop Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
        <GripVertical className="w-5 h-5 text-blue-600" />
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Drag the grip handles to reorder job postings.
        </p>
      </div>

      {/* Jobs List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No job postings yet</p>
            <Button onClick={() => openModal()} className="bg-cyan-600 hover:bg-cyan-700">
              Create Your First Job
            </Button>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={jobs.map(j => j.id)} strategy={verticalListSortingStrategy}>
              <div className="divide-y">
                {jobs.map((job) => (
                  <SortableJobItem
                    key={job.id}
                    job={job}
                    onTogglePublish={togglePublish}
                    onEdit={openModal}
                    onDelete={deleteJob}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* View Careers Page Link */}
      <div className="mt-6 text-center">
        <Link to="/careers" className="text-cyan-600 hover:text-cyan-700 font-medium">
          View Public Careers Page →
        </Link>
      </div>

      {/* Job Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">
                  {editingJob ? 'Edit Job Posting' : 'Create Job Posting'}
                </h2>
                <Button variant="ghost" onClick={closeModal}><X className="w-5 h-5" /></Button>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block">Job Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Lead Carpenter"
                  />
                </div>
                <div>
                  <Label className="mb-2 block">Department *</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(v) => setFormData({ ...formData, department: v })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Employment Type *</Label>
                  <Select
                    value={formData.employment_type}
                    onValueChange={(v) => setFormData({ ...formData, employment_type: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {employmentTypes.map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    placeholder="e.g., Charlotte Metro Area"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="mb-2 block">Salary Range (Optional)</Label>
                  <Input
                    value={formData.salary_range}
                    onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                    placeholder="e.g., $50,000 - $70,000 or $25-35/hour"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Job Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={4}
                  placeholder="Describe the role and what the ideal candidate looks like..."
                />
              </div>

              {/* Dynamic Lists */}
              {['responsibilities', 'requirements', 'benefits'].map((field) => (
                <div key={field}>
                  <Label className="mb-2 block capitalize">{field} {field !== 'benefits' && '*'}</Label>
                  {formData[field].map((item, index) => (
                    <div key={index} className="flex gap-2 mb-2">
                      <Input
                        value={item}
                        onChange={(e) => updateListItem(field, index, e.target.value)}
                        placeholder={`Add ${field.slice(0, -1)}...`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeListItem(field, index)}
                        className="text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => addListItem(field)}>
                    <Plus className="w-4 h-4 mr-1" /> Add {field.slice(0, -1)}
                  </Button>
                </div>
              ))}

              <div className="flex items-center gap-3 pt-4 border-t">
                <input
                  type="checkbox"
                  id="is_published"
                  checked={formData.is_published}
                  onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-300"
                />
                <Label htmlFor="is_published">Publish immediately (visible on careers page)</Label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700">
                  {editingJob ? 'Update Job' : 'Create Job'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminSidebar>
  );
};

export default AdminJobsPage;
